/* ============================================================
   Wiredan Vault Protocol - Media & Voice Call Utility
   Path: src/utils/mediaVaultHelper.js
   Status: PRODUCTION READY - OPTIMIZED FOR INSTANT UPLOAD & HD PLAYBACK
   ============================================================ */

import { useRef, useState } from 'react';
import toast from 'react-hot-toast';

export const R2_PUBLIC_URL = "https://pub-693a86d09ddb444096c7ff2a6d9d4754.r2.dev";

export const getMediaUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl;
  }
  return `${R2_PUBLIC_URL}/${pathOrUrl.replace(/^\//, '')}`;
};

export const useVoiceCall = (orderId, token, user) => {
  const [inCall, setInCall] = useState(false);
  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const peerConnection = useRef(null);

  const ICE_SERVERS = {
    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
  };

  const startVoiceCall = async () => {
    try {
      setInCall(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { echoCancellation: true, noiseSuppression: true, bitrate: 24000 } 
      });
      
      if (localAudioRef.current) localAudioRef.current.srcObject = stream;

      peerConnection.current = new RTCPeerConnection(ICE_SERVERS);
      stream.getTracks().forEach(track => peerConnection.current.addTrack(track, stream));

      peerConnection.current.ontrack = (event) => {
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = event.streams[0];
      };

      const offer = await peerConnection.current.createOffer();
      await peerConnection.current.setLocalDescription(offer);

      await fetch('/api/vault/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ 
          orderId, 
          senderId: user?.uuid || user?.id, 
          message: JSON.stringify({ type: 'VOICE_CALL_OFFER', sdp: offer }),
          message_type: 'signal'
        })
      });
      toast.success("Voice Call Initiated");
    } catch (err) {
      setInCall(false);
      toast.error("Could not start voice call. Check mic permissions.");
    }
  };

  const endVoiceCall = () => {
    if (peerConnection.current) peerConnection.current.close();
    setInCall(false);
    toast.success("Call Ended");
  };

  return { inCall, startVoiceCall, endVoiceCall, localAudioRef, remoteAudioRef };
};

export const uploadMediaFile = async (file, orderId, token, type, senderId = null) => {
  let processedFile = file;

  // Compress image uploads above 500KB
  if (type === 'image' && file.size > 500 * 1024) {
    processedFile = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1024;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          }, 'image/jpeg', 0.75);
        };
      };
    });
  }

  // 🛡️ Robust MIME type enforcement for video playback compatibility
  if (type === 'video') {
    const safeType = file.type && file.type.startsWith('video/') ? file.type : 'video/mp4';
    processedFile = new File([file], file.name || 'evidence_video.mp4', { type: safeType });
  }

  const formData = new FormData();
  formData.append('file', processedFile);
  formData.append('orderId', orderId);
  formData.append('type', type);

  // Resolve and append senderId from agritrade_user or wiredan storage to match database requirements accurately
  let resolvedSenderId = senderId;
  if (!resolvedSenderId) {
    try {
      const storedUser = JSON.parse(localStorage.getItem('agritrade_user') || localStorage.getItem('wiredan_user') || '{}');
      resolvedSenderId = storedUser?.uuid || storedUser?.id || storedUser?.email || '';
    } catch (_) {}
  }
  if (resolvedSenderId) {
    formData.append('senderId', resolvedSenderId);
  }

  // Extended timeout wrapper (300 seconds / 5 minutes) to ensure HD video files upload safely
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 300000);

  try {
    const res = await fetch('/api/vault/upload', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Upload failed on server");
    return data.fileUrl;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error("Upload timed out. The video file might be too large or your connection is unstable.");
    }
    throw err;
  }
};